import React, { useState } from 'react';
import api from '../api';
import { useTranslation } from '../LanguageContext';

/**
 * Report an account. Reachable from a profile; the reported user is never
 * told a report exists, so nothing here confirms or denies past reports.
 *
 * Reason keys mirror `Report.REASON_CHOICES` on the backend — adding one here
 * without adding it there gets rejected as `invalid_reason`.
 */
export const REPORT_REASONS = [
    'spam', 'harassment', 'scam', 'impersonation',
    'inappropriate', 'fake_followers', 'no_delivery', 'other',
];

function ReportModal({ username, userId, onClose }) {
    const { t } = useTranslation();
    const [reason, setReason] = useState('');
    const [description, setDescription] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    // "Other" alone tells a moderator nothing they can act on.
    const needsDetail = reason === 'other';
    const valid = reason && (!needsDetail || description.trim());

    const submit = async () => {
        if (!valid || busy) return;
        setBusy(true);
        setError('');
        try {
            await api.post('/api/report-user/', {
                username, user_id: userId, reason, description,
            });
            setDone(true);
        } catch (err) {
            const code = err.response?.data?.error;
            setError(
                code === 'already_reported' ? t('report.alreadyReported')
                    : code === 'cannot_report_self' ? t('report.cannotReportSelf')
                        : code === 'description_required' ? t('report.detailRequired')
                            : t('report.error')
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9700] bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden">

                <div className="flex items-start justify-between px-6 pt-6 pb-3 flex-shrink-0">
                    <div className="min-w-0">
                        <h3 className="text-lg font-black text-slate-900">{t('report.title')}</h3>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">
                            {t('report.subtitle')} <strong className="text-slate-700">{username}</strong>
                        </p>
                    </div>
                    <button onClick={onClose}
                        className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 transition flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="px-6 pb-6 overflow-y-auto custom-scrollbar">
                    {done ? (
                        <div className="text-center py-4">
                            <div className="w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-3">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <p className="text-base font-black text-slate-900">{t('report.sent')}</p>
                            <p className="text-xs text-slate-500 mt-1 leading-relaxed px-2">{t('report.sentNote')}</p>
                            <button onClick={onClose} className="btn-primary w-full py-2.5 text-sm mt-5">
                                {t('report.close')}
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <p className="text-xs font-semibold text-slate-500">{t('report.reasonLabel')}</p>
                            <div className="space-y-1.5">
                                {REPORT_REASONS.map(key => (
                                    <label key={key}
                                        className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${reason === key
                                            ? 'border-primary-300 bg-primary-50'
                                            : 'border-slate-200 hover:bg-slate-50'}`}>
                                        <input type="radio" name="report-reason" value={key}
                                            checked={reason === key}
                                            onChange={() => { setReason(key); setError(''); }}
                                            className="mt-0.5 w-4 h-4 accent-[#20c9b5] flex-shrink-0" />
                                        <span className="text-sm text-slate-700 leading-snug">
                                            {t(`report.reason_${key}`)}
                                        </span>
                                    </label>
                                ))}
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-500 mb-1.5" htmlFor="report-detail">
                                    {t('report.detailLabel')}
                                    {needsDetail && <span className="text-red-500"> *</span>}
                                </label>
                                <textarea id="report-detail"
                                    className="input min-h-[90px] resize-y text-sm"
                                    value={description}
                                    maxLength={2000}
                                    placeholder={t('report.detailPlaceholder')}
                                    onChange={(e) => setDescription(e.target.value)} />
                            </div>

                            {error && (
                                <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                                    {error}
                                </p>
                            )}

                            <div className="flex gap-2 pt-1">
                                <button onClick={onClose} className="btn-secondary flex-1 py-2.5 text-sm">
                                    {t('report.cancel')}
                                </button>
                                <button onClick={submit} disabled={!valid || busy}
                                    className="btn-danger flex-1 py-2.5 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40">
                                    {busy && <span className="spinner-sm" />}
                                    {t('report.submit')}
                                </button>
                            </div>

                            <p className="text-[11px] text-slate-400 leading-relaxed">{t('report.privacyNote')}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ReportModal;
